import { getPermissions, getSettingInfo, getTeams, getUserInfo } from '@/api';
import '@/assets/style/global.less';
import Header from '@/component/header';
import Team from '@/component/team';
import useRuntimeInfo from '@/hooks/useRuntimeInfo';
import {
  getUser,
  removePermissions,
  removeSettingInfo,
  removeTeams,
  removeUser,
  setPermissions,
  setSettingInfo,
  setTeams,
  setUser,
  useAddUserEventListener,
} from '@/utils/helper';
import { Layout, Loading, Sidebar, SvgIcon } from '@zenlayer/zen-design';
import {
  logo_LogoHasText_partner_dark_str,
  logo_LogoHasText_partnerX_dark_str,
} from '@zenlayer/zen-design-icons';
import { useRequest } from 'ahooks';
import qs from 'query-string';
import { unnest } from 'ramda';
import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { getMenus, router } from './router';

const Main = () => {
  const location = useLocation();

  const UserRequest = useRequest(
    async () => {
      try {
        const res = await getUserInfo();
        const { data, success } = res;
        if (success) {
          setUser(data);
          await Promise.resolve({
            then: function (resolve: any, reject: any) {
              //设置User是异步的，所以确保设置成功后结束loading
              const Timer = setInterval(() => {
                if (getUser()) {
                  clearInterval(Timer);
                  resolve?.(true);
                }
              }, 100);
            },
          });
        } else {
          removeUser();
        }
        return res;
      } catch (error) {
        removeUser();
      }
      return undefined;
    },
    {
      onSuccess: (res) => {
        //原本是可以并行的，值因为csrf的token是在user里返回的
        if (res?.success) {
          TeamsRequest.run();
          PermissionsRequest.run();
          SettingInfoRequest.run();
        }
      },
    }
  );

  const TeamsRequest = useRequest(getTeams, {
    manual: true,
    onSuccess: (res) => {
      if (res.success) {
        setTeams(res.data);
      } else {
        removeTeams();
        removeUser(); // 让他重新回去登陆
      }
    },
  });

  const PermissionsRequest = useRequest(getPermissions, {
    manual: true,
    onSuccess: (res) => {
      if (res.success) {
        setPermissions(unnest(Object.values(res.data)));
      } else {
        removePermissions();
        removeUser(); // 让他重新回去登陆
      }
    },
  });

  const SettingInfoRequest = useRequest(getSettingInfo, {
    manual: true,
    onSuccess: (res) => {
      if (res.success) {
        setSettingInfo(res.data);
      } else {
        removeSettingInfo();
        removeUser(); // 让他重新回去登陆
      }
    },
  });

  const user = useAddUserEventListener();

  if (
    UserRequest.loading ||
    TeamsRequest.loading ||
    PermissionsRequest.loading ||
    SettingInfoRequest.loading
  ) {
    return <Loading.Portal />;
  }

  if (!user) {
    return (
      <Navigate
        to={`/auth/login?${qs.stringify({
          redirectTo: location.pathname + location.search,
        })}`}
        replace
      />
    );
  }

  return (
    <Layout
      header={<Header />}
      sidebar={
        <Sidebar
          hasTextLogo={<SvgIcon svgStr={logo_LogoHasText_partnerX_dark_str} />}
          logo={<SvgIcon svgStr={logo_LogoHasText_partner_dark_str} />}
          responsive="l"
          mainMenus={getMenus()}
          team={<Team />}
        />
      }
    >
      <Routes>
        {router.map((i) => {
          return <Route key={i.path} path={i.path} element={<i.component />} />;
        })}

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

const AuthPage = lazy(() => import('@/page/auth/index'));

export default () => {
  const { ready } = useRuntimeInfo();

  return (
    <BrowserRouter>
      <Suspense fallback={<Loading.Portal />}>
        {ready && (
          <Routes>
            <Route path="/auth/*" element={<AuthPage />} />
            <Route path="*" element={<Main />} />
          </Routes>
        )}
      </Suspense>
    </BrowserRouter>
  );
};
